import { CreativeSectionConfiguration } from "@/types";

export interface PresetItem {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  isAI?: boolean;
  formats?: ("video" | "image")[];
  isCustom?: boolean;
  /** Prompt sent to the API when auto-creating a label for this AI preset */
  labelPrompt?: string;
  /** Preset configuration that overrides the default section configuration */
  configuration?: Partial<CreativeSectionConfiguration>;
}

export const presetItems: PresetItem[] = [
  {
    id: "custom-section",
    title: "Custom section",
    description:
      "Create an AI powered section that groups creatives for you. Just describe in natural language what you want to group by, based on what the ad shows or any naming fields like ad name or campaign name. The AI will automatically organize everything into clear, simple groups.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1ef40f5de2962c9b4015_Image%201.webp",
    isAI: true,
    isCustom: true,
  },
  {
    id: "creatives",
    title: "Creatives",
    description:
      "Same creatives are grouped together, even if they live under different ads, ad sets, campaigns, or ad accounts. Chat with Creative is available here to help you ask questions with creative and brainstorm next variations.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc885fec21e6d46245c_Image%202.webp",
    configuration: {
      dropdowns: { group_by: "creative_name" },
      additional_config: { view_mode: "creative_view" },
    },
  },
  {
    id: "recently-launched",
    title: "Recently launched",
    description:
      "Monitor the ads which are launched in last 7 days to track their performance to make better decisions and make more variation of working creatives.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc84ff822e68a94e621_Image%203.webp",
    configuration: {
      filters: [
        {
          key: "start_date",
          field: "start_date",
          label: "Ad started",
          filter_type: "date",
          conditions: [
            {
              operator: "DATE_ON_OR_AFTER",
              label: "Ad started",
              key: "start_date",
              parent_key: "start_date",
              value: {
                start_date: "",
                end_date: "",
                label: "Last 7 days" as import("@/types").TimeInterval,
              },
            },
          ],
          filter_on: "ads" as import("@/types").FilterOn,
        },
      ],
    },
  },
  {
    id: "ad-name",
    title: "Ad name",
    description:
      "Monitor the ads based on Ad name, ads with same ad name would be group together to track.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc83f6a3706830c564d_Image%204.webp",
    configuration: { dropdowns: { group_by: "ad_name" } },
  },
  {
    id: "adsets",
    title: "Adsets",
    description:
      "Monitor the ads based on Adsets, ads within same adset would be group together to track.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8092cff2819f762d5_Image%205.webp",
    configuration: { dropdowns: { group_by: "adset_id" } },
  },
  {
    id: "campaigns",
    title: "Campaigns",
    description:
      "Monitor the ads based on Campaigns, ads within same campaign would be group together to track.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc890d553cdcb1142a4_Image%206.webp",
    configuration: {
      dropdowns: { group_by: "campaign_id" },
      additional_config: { view_mode: "bar_chart_view" },
    },
  },
  {
    id: "messaging",
    title: "Messaging",
    description:
      "Messaging is the ad's main message/angle, what it wants viewers to take away. Ads go in the same group when the message matches. This helps you quickly compare different creative versions of the same idea.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc80ee71a3d07aaf8f4_Image%207.webp",
    isAI: true,
    formats: ["video", "image"],
    labelPrompt:
      "Cluster them based on messaging: the main message/angle each ad communicates and how it frames value. \n\nFor each ad, infer: \nPrimary messaging (core claim/angle) and Messaging sub: motive (intended shift in the viewer—feel/believe/do). Use human nuance (subtext: insecurity/aspiration/friction), not literal claims. If multiple messages, pick one primary. Create as many groups as needed to reflect distinct messaging, but merge near-duplicates and keep groups clear. Group names must be 2–5 words, plain English, instantly clear (no jargon).",
  },
  {
    id: "hook-type",
    title: "Hook type",
    description:
      "Hook type is the opening that grabs attention in the first moments. Ads go in the same group when they use the same hook style. This helps you quickly compare different ways to grab attention.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc80ca512a683bdfb2a_Image%208.webp",
    isAI: true,
    formats: ["video"],
    labelPrompt:
      "Cluster them based on hook type, what makes someone stop in the first moments.\n\nFor each ad, infer:\nPrimary hook type (question, pain callout, surprising fact, warning, transformation, quick demo, social proof, price shock, curiosity setup, pattern interrupt)\nHook sub: trigger (why it works, curiosity, surprise, fear, relief, status, urgency)\nUse human nuance, focus on what is shown or said first, not the full story. If multiple hooks, pick one primary. Create as many groups as needed, merge near duplicates, keep group names 2 to 5 words, plain English.",
  },
  {
    id: "video-format",
    title: "Video format",
    description:
      "Video format is the ad's filming and style template. Ads go in the same group when they are shot and edited in a similar way. This helps you quickly compare different formats side by side.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8f27135a063b42c8b_Image%209.webp",
    isAI: true,
    formats: ["video"],
    labelPrompt:
      "Cluster them based on video format, meaning the recurring content template and production style, such as talking head UGC, vlog style, unboxing, screen recording, product demo, testimonial, montage with b roll, interview, skit, or animated explainer. \n\nFor each ad, infer:\nPrimary video format, the main format template, and Format sub: style cues, camera setup, pacing, edits, text overlays, sound design, and setting. Focus on how it is made, not what it says. If multiple formats appear, pick one primary. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English.",
  },
  {
    id: "background-music",
    title: "Background music",
    description:
      "Background music is the sound behind the ad that sets the mood and feeling. Ads go in the same group when the music has a similar vibe. This helps you quickly compare which music vibes you use most, and spot new ones to test.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8523abacb44a6a774_Image%2010.webp",
    isAI: true,
    formats: ["video"],
    labelPrompt:
      "Cluster them based on background music, meaning the simple music vibe a normal viewer notices fast, like upbeat and fun, calm and chill, emotional and heartfelt, cinematic and epic, dark and tense, tech and modern, playful and quirky, trending audio, or no background music. Music mainly sets the mood, so keep groups broad and easy to scan.\n\nFor each ad, infer Primary background music, the main vibe, and Music sub: mood and energy. Focus on what it feels like, not music theory. If multiple vibes appear, pick one primary. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English, instantly clear.",
  },
  {
    id: "concept",
    title: "Concept",
    description:
      "Concept is the big idea or storyline the ad is built around. Ads go in the same group when they share the same concept. This helps you quickly compare different executions of the same core idea.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc880b0bb9c1cab6299_Image%2011.webp",
    isAI: true,
    formats: ["video", "image"],
    labelPrompt:
      "Cluster them based on concept, meaning the big idea or storyline the ad is built around, like before and after, day in the life, problem to solution, comparison, challenge or experiment, testimonial story, founder story, myth busting, skit scenario, or visual metaphor. A concept is the overarching big idea or story behind the ad.\n\nFor each ad, infer:\nPrimary concept, the single best label for the main idea. Focus on the idea and storyline, not the hook type, message, or music. If multiple concepts appear, pick one primary. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English, instantly clear.",
  },
  {
    id: "tone-delivery-style",
    title: "Tone & Delivery style",
    description:
      "Tone and delivery style is how the ad sounds and feels to a viewer. Ads go in the same group when the attitude and energy match. This helps you quickly compare different delivery styles side by side.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8be4d2ecec82e81bb_Image%2012.webp",
    isAI: true,
    formats: ["video"],
    labelPrompt:
      "Cluster them based on Tone and Delivery style, meaning how it sounds to a normal viewer.\n\nTone is the attitude and emotion, like friendly, confident, calm, urgent, playful, emotional, bold, or neutral. Delivery is energy and pacing, like low energy, medium, or high energy.\n\nDo not create groups based on format or production type, such as podcast, interview, skit, cinematic, text only, montage, or tech demo. Those belong to other categories. If an ad has no spoken delivery, still assign a tone based on the writing and mood, like neutral, urgent, playful, or emotional.\n\nFor each ad, infer Primary tone and delivery style, the single best label. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English, instantly clear.",
  },
  {
    id: "video-duration",
    title: "Video duration",
    description:
      "Video duration is how long the ad runs from start to finish. Ads go in the same group when they fall into the same time range. This helps you quickly compare what works across short vs long ads.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc88d08f3e1d6b67590_Image%2013.webp",
    isAI: true,
    formats: ["video"],
    labelPrompt:
      "Cluster them based on video duration, using simple length buckets like 0 to 6 seconds, 7 to 15 seconds, 16 to 30 seconds, 31 to 60 seconds, and over 60 seconds. \n\nFor each ad, infer Primary duration bucket, the single best fit based on total runtime. If a video sits on a boundary, choose the closer bucket. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English, instantly clear.",
  },
  {
    id: "target-persona",
    title: "Target persona",
    description:
      "Target persona is who the ad is meant for, based on simple cues like life stage, role, or need. Ads go in the same group when they target a similar audience, even if the message or format is different. This helps you quickly see which audiences you are speaking to most.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc81ba6a76bb2d2e653_Image%2014.webp",
    isAI: true,
    formats: ["video", "image"],
    labelPrompt:
      "Cluster them based on Target persona, meaning who the ad is meant for, using broad, standard audience buckets.\n\nFor each ad, infer Primary target persona using only simple labels based on role or life stage plus one need, like students, young professionals, parents, seniors, small business owners, travelers, budget shoppers, privacy concerned users, or online daters.\n\nDo not create niche personas or overly descriptive labels, like spam weary everyday adults or digital minimalists and wellness seekers. Keep personas general and easy to scan. If multiple personas appear, pick one primary. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English, instantly clear.",
  },
  {
    id: "value-prop",
    title: "Value prop",
    description:
      "Value proposition is the main benefit the ad promises, and why the product is worth choosing. This helps you quickly compare which benefits you lead with most, and spot new ones to test.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8079643c40ad63225_Image%2015.webp",
    isAI: true,
    formats: ["video", "image"],
    labelPrompt:
      "Cluster them based on value proposition, meaning the specific value the product promises and why it is worth choosing, like saving time, saving money, better results, less risk, more convenience, more privacy, or better quality.\n\nFor each ad, infer Primary value proposition, the single best label for the main promised benefit. Focus on the customer outcome and the promise of value, not the hook type, tone, format, or concept. If multiple benefits appear, pick one primary. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English, instantly clear.",
  },
  {
    id: "color-palette",
    title: "Color palette",
    description:
      "Color palette is the overall color look of the ad, based on the dominant colors and how bright or dark it feels. Ads go in the same group when the palette looks similar at a glance. This helps you quickly compare broad color styles across your creatives.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8c0f96320590327b3_Image%2016.webp",
    isAI: true,
    formats: ["video", "image"],
    labelPrompt:
      "Cluster them based on color palette, meaning the big, easy to recognize color look of the ad.\n\nUse broad groups like warm tones, cool tones, neutral tones, black and white, bright and colorful, dark and moody, pastel soft, and high contrast. Focus on the dominant colors and overall feel, not specific shades, gradients, or niche styles. Keep group names 2 to 5 words, plain English, instantly clear. Create as many groups as needed and merge near duplicates.",
  },
  {
    id: "creator",
    title: "Creator",
    description:
      "Creator is who the viewer sees delivering the ad, like a customer, influencer, expert, founder, employee, or a voiceover narrator. Ads go in the same group when the creator type feels similar. This helps you quickly compare which creator types you use most across your creatives.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc885fec21e6d462459_Image%2017.webp",
    isAI: true,
    formats: ["video"],
    labelPrompt:
      "Cluster them based on creator type, meaning the kind of person delivering the ad and their role, such as founder, employee, customer testimonial, expert, influencer creator, actor, narrator voice, or no person shown. \n\nKeep creator groups broad and standard, do not create niche labels or combine multiple traits into long names. Focus on who the viewer perceives is delivering the message, not the video format, hook, concept, or tone. If multiple people appear, pick the primary one driving the ad. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English, instantly clear.",
  },
  {
    id: "emotional-tone",
    title: "Emotional tone",
    description:
      "Emotional tone is the main feeling the ad creates in the viewer, like joy, trust, fear, surprise, or calm. Ads go in the same group when they aim for a similar emotion. This helps you quickly compare which emotions your ads lean on most across creatives.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8ecea3ca4443f9754_Image%2018.webp",
    isAI: true,
    formats: ["video", "image"],
    labelPrompt:
      "Cluster them based on emotional tone, meaning the main feeling the ad is trying to create in the viewer, like joy, trust, fear, surprise, sadness, disgust, anger, or anticipation.\n\nFor each ad, infer Primary emotional tone, the single best label for the dominant emotion. Focus on the emotional mood, not the message, hook type, format, music, or creator. If multiple emotions appear, pick one primary. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English, instantly clear.",
  },
  {
    id: "narrative-style",
    title: "Narrative style",
    description:
      "Narrative style is how the ad tells its story from start to finish. Ads go in the same group when they follow a similar story flow. This helps you quickly compare different storytelling structures side by side.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc845ab30257af084d4_Image%2019.webp",
    isAI: true,
    formats: ["video"],
    labelPrompt:
      "Cluster them based on narrative style, meaning how the story is told and structured, such as problem to solution, before and after, day in the life, testimonial story, comparison, myth busting, step by step walkthrough, journey story, or reveal twist.\n\nFor each ad, infer Primary narrative style, the single best label for the storytelling structure. Focus on the story flow and sequence, not the message, hook type, format, music, creator, or tone. If multiple narrative styles appear, pick one primary. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English, instantly clear.",
  },
  {
    id: "product-type",
    title: "Product type",
    description:
      "Product type is the category of the product shown in the ad, like skincare, fitness app, insurance, or SaaS tool. Ads go in the same group when they promote a similar type of product. This helps you quickly compare performance across product categories.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8df960b7695f8e7c5_Image%2020.webp",
    isAI: true,
    formats: ["video", "image"],
    labelPrompt:
      "Cluster them based on Product type, meaning what kind of product or service is being promoted, using clear category labels a normal viewer would use, like dating app, finance app, insurance, credit card, ecommerce, food delivery, fitness app, skincare, SaaS tool, or education course. \n\nProduct type is the product category shown in the ad, not the message, tone, format, concept, creator, or music. If multiple products appear, pick the primary one being sold. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English, instantly clear.",
  },
  {
    id: "visual-composition",
    title: "Visual composition",
    description:
      "Visual composition describes how an image ad is laid out and what your eye notices first. Ads go in the same group when the layout and visual hierarchy look similar at a glance. This helps you quickly compare broad layout styles across your image creatives.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8b4278544f47787a9_Image%2021.webp",
    isAI: true,
    formats: ["image"],
    labelPrompt:
      "Cluster them based on visual composition, meaning how the layout is arranged and what the viewer's eye notices first, such as centered hero, product close up, text led layout, split screen, grid collage, lifestyle scene, minimal whitespace, or busy layered layout. \n\nFocus on layout structure and visual hierarchy, not color palette, message, or concept. A good composition grouping is based on focal point placement, balance, alignment, spacing, and how elements are grouped. If multiple layouts appear, pick one primary. Create as many groups as needed, merge near duplicates, and keep group names 2 to 5 words, plain English, instantly clear.",
  },
  {
    id: "age",
    title: "Age",
    description:
      "Monitor ads by Age. Ads targeting the same age range will be grouped together so you can track performance by age segment.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8e7a50265c857f2ea_Image%2023.webp",
    configuration: {
      dropdowns: { group_by: "age" },
      additional_config: { view_mode: "bar_chart_view" },
    },
  },
  {
    id: "gender",
    title: "Gender",
    description:
      "Monitor the ads by Gender. Ads targeting the same gender will be grouped together so you can track performance by gender segment.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8796fb911ebd54d21_Image%2024.webp",
    configuration: {
      dropdowns: { group_by: "gender" },
      additional_config: { view_mode: "bar_chart_view" },
    },
  },
  {
    id: "country",
    title: "Country",
    description:
      "Monitor the ads by Country. Ads targeting the same country will be grouped together so you can track performance by country segment.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc878a485c927322d96_Image%2025.webp",
    configuration: { dropdowns: { group_by: "country" } },
  },
  {
    id: "ad-copy",
    title: "Ad copy",
    description:
      "Monitor the ads by Ad copy. Ads with the same or very similar ad copy will be grouped together so you can track performance by copy theme.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc87ab031e02e8d1eed_Image%2026.webp",
    configuration: { dropdowns: { group_by: "body_asset" } },
  },
  {
    id: "landing-page",
    title: "Landing page",
    description:
      "Monitor the ads by Landing page. Ads that drive to the same landing page will be grouped together so you can track performance by destination page.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc8400366fb53b30701_Image%2027.webp",
    configuration: {
      dropdowns: { group_by: "landing_pages" },
      additional_config: { view_mode: "bar_chart_view" },
    },
  },
  {
    id: "media-type",
    title: "Media type",
    description:
      "Monitor the ads by Media type. Ads with the same media type, like image or video, will be grouped together so you can track performance by creative format.",
    thumbnail:
      "https://cdn.prod.website-files.com/65783e649367bc55fecaea2d/698f1cc80889bda15481edd0_Image%2028.webp",
    configuration: {
      dropdowns: { group_by: "media_type" },
      additional_config: { view_mode: "creative_share_view" },
    },
  },
];
